"use client";

import { useBooking } from "@/contexts/BookingContext";
import { Clock, ChevronLeft, ChevronRight, ArrowBigRight } from "lucide-react";
import { BookingStep } from "@/types/booking";
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { formatDuration, formatPrice, getEffectiveTotalDurationMinutes, getBusinessCurrency } from "@/lib/utils";
import { useLazySalonAvailability } from "@/lib/apollo/hooks/booking-edge";
import { useScrollToTop } from "@/hooks/useScrollToTop";
import { useAvailabilityPolling } from "@/hooks/useAvailabilityPolling";
import {
  useGetAvailabilityNext,
  AvailabilityNextRequest,
  AvailabilityService,
} from "@/lib/api/usePublicV2";

import CartSummary from "@/components/booking/CartSummary";
import SvgClosed from "@/components/ui/svg_closed";
import StaffNotAvailableImage from "@/components/ui/svg_no_staff";
import { Button } from "@/components/ui/button";
import {
  addDaysToDateOnly,
  dateOnlyFromInstant,
  encodeScheduledWall,
  formatDateOnly,
  formatDateOnlyForDisplay,
  nextSalonDateBoundary,
  parseDateOnly,
  salonTimeZoneLabel,
  weekdayForDateOnly,
} from "@/lib/salon-time";

function useSalonToday(timeZone: string) {
  const getToday = useCallback(
    () => dateOnlyFromInstant(new Date(), timeZone),
    [timeZone],
  );
  const [today, setToday] = useState(getToday);

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;

    const refresh = () => setToday(getToday());
    const scheduleBoundaryRefresh = () => {
      clearTimeout(timeout);
      const boundary = nextSalonDateBoundary(timeZone);
      const delay = Math.max(1_000, boundary.getTime() - Date.now() + 100);
      timeout = setTimeout(() => {
        refresh();
        scheduleBoundaryRefresh();
      }, Math.min(delay, 2_147_483_647));
    };
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        refresh();
        scheduleBoundaryRefresh();
      }
    };
    const handleFocus = () => {
      refresh();
      scheduleBoundaryRefresh();
    };

    refresh();
    scheduleBoundaryRefresh();
    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      clearTimeout(timeout);
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [getToday, timeZone]);

  return useMemo(() => parseDateOnly(today)!, [today]);
}

export default function TimeAndDate() {
  useScrollToTop();
  const {
    selectedServices,
    selectedStaff,
    setAppointmentDateTime,
    handleNextStep,
    businessInfo,
    selectedSalon,
    addToast,
    selectedDate: contextSelectedDate,
    selectedTime: contextSelectedTime,
    setSelectedDate: setContextSelectedDate,
    setSelectedTime: setContextSelectedTime,
    clearSelectedDateTime,
    setSlotSelectionTime,
    shouldFindNextAvailable,
    setShouldFindNextAvailable,
  } = useBooking();

  const salonTimezone = selectedSalon!.timezone;
  const salonToday = useSalonToday(salonTimezone);

  const [selectedDate, setSelectedDate] = useState<Date>(() => {
    // If returning to this step with a previously selected date, use it
    if (contextSelectedDate) {
      return parseDateOnly(contextSelectedDate) || salonToday;
    }
    return salonToday;
  });
  const [selectedTime, setSelectedTime] = useState<string>(contextSelectedTime || "");
  const [availableSlots, setAvailableSlots] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [availabilityReason, setAvailabilityReason] = useState<string | null>(
    null
  );
  const [allDays, setAllDays] = useState<Date[]>([]);
  const [currentMonthEnd, setCurrentMonthEnd] = useState(salonToday);
  const [visibleMonth, setVisibleMonth] = useState(salonToday); // Track the currently visible month
  const [searchingNextAvailable, setSearchingNextAvailable] = useState(false);
  const [nextAvailabilityRequest, setNextAvailabilityRequest] =
    useState<AvailabilityNextRequest | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isLoadingMore = useRef(false);
  const preservedScrollPosition = useRef<number>(0);
  const isFetchingAvailability = useRef(false);
  // Guard to ensure we only handle a given next_available_slot once
  const handledNextAvailableKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (selectedDate < salonToday) {
      setSelectedDate(salonToday);
      setSelectedTime("");
      setAppointmentDateTime(null);
      clearSelectedDateTime();
    }
  }, [
    clearSelectedDateTime,
    salonToday,
    selectedDate,
    setAppointmentDateTime,
  ]);

  // GraphQL query for availability
  const [getAvailability] = useLazySalonAvailability();

  // Callback when slot becomes unavailable (detected by polling)
  const handleSlotUnavailable = useCallback(() => {
    // Clear the selected time in context
    clearSelectedDateTime();
    // Clear local selection
    setSelectedTime("");
    // Show toast notification
    addToast({
      message: "Sorry, this time slot is no longer available. Please select another time.",
      variant: "warning",
    });
  }, [clearSelectedDateTime, addToast]);

  // Availability polling hook - polls every 60 seconds when user has selected a time
  // This helps detect if another user booked the slot (race condition prevention)
  const { checkNow: checkAvailabilityNow } = useAvailabilityPolling({
    salonId: selectedSalon?.id || null,
    services: selectedServices,
    staffId: selectedStaff?.id || null,
    selectedDate: contextSelectedDate,
    selectedTime: contextSelectedTime,
    enabled: !!contextSelectedTime && !!contextSelectedDate, // Only poll when a slot is selected
    pollingInterval: 60000, // 60 seconds
    onSlotUnavailable: handleSlotUnavailable,
  });

  // Check availability immediately when returning to this step (if a time was selected)
  useEffect(() => {
    if (contextSelectedTime && contextSelectedDate) {
      // Small delay to ensure component is mounted
      const timer = setTimeout(() => {
        checkAvailabilityNow();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, []); // Only run on mount

  // REST API hook for finding next available date
  const {
    data: nextAvailabilityData,
    loading: nextAvailabilityLoading,
    error: nextAvailabilityError,
  } = useGetAvailabilityNext(nextAvailabilityRequest);

  // Calculate total duration and price
  const effectiveScheduleType =
    selectedServices.length > 0 &&
    selectedServices.every(
      (s: any) => String(s?.bundleScheduleType || "sequence").toLowerCase() === "parallel"
    )
      ? "parallel"
      : "sequence";
  const totalDuration = getEffectiveTotalDurationMinutes({
    scheduleType: effectiveScheduleType,
    services: selectedServices,
  });
  // Match CartSummary: prefer staff-specific custom price when set (non-bundle services)
  const totalPrice = selectedServices.reduce((sum, service) => {
    const unitPrice = service.bundleId
      ? service.price || 0
      : service.staffPrice || service.price || 0;
    return sum + unitPrice * (service.quantity || 1);
  }, 0);

  // Calculate the maximum bookable date based on business settings (0 means no cap)
  const maxAdvanceBookingDays = businessInfo?.maxAdvanceBookingDays || 0;
  const maxBookableDate = useMemo(() => {
    if (maxAdvanceBookingDays === 0) return null;
    return addDaysToDateOnly(salonToday, maxAdvanceBookingDays);
  }, [maxAdvanceBookingDays, salonToday]);

  const isBeyondMaxDate = useCallback(
    (date: Date) => maxBookableDate !== null && date > maxBookableDate,
    [maxBookableDate]
  );

  // Generate days for a specific month starting from a given date
  const getDaysForMonth = useCallback((startDate: Date) => {
    const days = [];
    const year = startDate.getUTCFullYear();
    const month = startDate.getUTCMonth();

    // Start from the given date (could be middle of month for current month)
    const startDay = startDate.getUTCDate();

    // Get the last day of the month
    const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();

    for (let day = startDay; day <= lastDay; day++) {
      const currentDate = new Date(Date.UTC(year, month, day));
      // Only include dates from today onwards and within max advance booking window
      if (
        currentDate >= salonToday &&
        !isBeyondMaxDate(currentDate)
      ) {
        days.push(currentDate);
      }
    }

    return days;
  }, [isBeyondMaxDate, salonToday]);

  // Generate all days for the next month
  const getDaysForNextMonth = useCallback((fromDate: Date) => {
    const days = [];

    // Create next month by setting date to 1st first, then incrementing month
    // This prevents issues with month overflow (e.g., Oct 31 -> Nov 31 -> Dec 1)
    const nextMonth = new Date(
      Date.UTC(fromDate.getUTCFullYear(), fromDate.getUTCMonth() + 1, 1),
    );

    const year = nextMonth.getUTCFullYear();
    const month = nextMonth.getUTCMonth();
    const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();

    for (let day = 1; day <= lastDay; day++) {
      const currentDate = new Date(Date.UTC(year, month, day));
      if (!isBeyondMaxDate(currentDate)) {
        days.push(currentDate);
      }
    }

    return days;
  }, [isBeyondMaxDate]);

  // Load more days (next month) function
  const loadMoreDays = useCallback(() => {
    if (isLoadingMore.current) return;
    // Stop loading more if we've reached the max bookable date
    if (isBeyondMaxDate(currentMonthEnd)) return;

    isLoadingMore.current = true;

    // Save current scroll position before adding new content
    if (scrollContainerRef.current) {
      preservedScrollPosition.current = scrollContainerRef.current.scrollLeft;
    }

    // Generate next month's days
    const nextMonthDays = getDaysForNextMonth(currentMonthEnd);

    if (nextMonthDays.length > 0) {
      setAllDays((prev) => {
        const newDays = [...prev, ...nextMonthDays];

        // Restore scroll position after state update
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (scrollContainerRef.current) {
              scrollContainerRef.current.scrollLeft =
                preservedScrollPosition.current;
            }
          });
        });

        return newDays;
      });

      // Update the current month end to the last day of the newly added month
      const lastNewDay = nextMonthDays[nextMonthDays.length - 1];
      setCurrentMonthEnd(lastNewDay);
    }

    // Small delay to prevent rapid successive calls
    setTimeout(() => {
      isLoadingMore.current = false;
    }, 100);
  }, [currentMonthEnd, getDaysForNextMonth]);

  // Update visible month based on scroll position
  const updateVisibleMonth = useCallback(() => {
    if (!scrollContainerRef.current || allDays.length === 0) return;

    const container = scrollContainerRef.current;
    const scrollLeft = container.scrollLeft;
    const containerWidth = container.clientWidth;

    // Calculate the center point of the visible area
    const centerScrollPosition = scrollLeft + containerWidth / 2;

    // Find which date element is at the center
    const dateElements = container.children;
    let visibleDateIndex = 0;
    let accumulatedWidth = 0;

    for (let i = 0; i < dateElements.length; i++) {
      const element = dateElements[i] as HTMLElement;
      const elementWidth = element.offsetWidth + 12; // Include gap (12px = 3 * 4px)

      if (accumulatedWidth + elementWidth > centerScrollPosition) {
        visibleDateIndex = i;
        break;
      }

      accumulatedWidth += elementWidth;
    }

    // Ensure we don't go out of bounds
    visibleDateIndex = Math.min(visibleDateIndex, allDays.length - 1);

    if (allDays[visibleDateIndex]) {
      const currentVisibleDate = allDays[visibleDateIndex];
      const currentVisibleMonth = new Date(
        Date.UTC(
          currentVisibleDate.getUTCFullYear(),
          currentVisibleDate.getUTCMonth(),
          1,
        ),
      );

      // Only update if the month has actually changed
      if (
        visibleMonth.getUTCMonth() !== currentVisibleMonth.getUTCMonth() ||
        visibleMonth.getUTCFullYear() !== currentVisibleMonth.getUTCFullYear()
      ) {
        setVisibleMonth(currentVisibleMonth);
      }
    }
  }, [allDays, visibleMonth]);

  // Initialize with current month days
  useEffect(() => {
    const currentMonthDays = getDaysForMonth(salonToday);

    if (currentMonthDays.length > 0) {
      setAllDays(currentMonthDays);
      // Set the end to the last day of current month
      const lastDay = currentMonthDays[currentMonthDays.length - 1];
      setCurrentMonthEnd(lastDay);
      // Initialize visible month to current month
      setVisibleMonth(
        new Date(
          Date.UTC(salonToday.getUTCFullYear(), salonToday.getUTCMonth(), 1),
        ),
      );

      // Load next month's days on mount for better user experience
      setTimeout(() => {
        loadMoreDays();
      }, 100);
    }
  }, [getDaysForMonth, salonToday]);

  // Update visible month when allDays changes (after loading more content)
  useEffect(() => {
    if (allDays.length > 0) {
      // Small delay to ensure DOM is updated after new content is added
      // Only update if we're not in the middle of loading more content
      setTimeout(() => {
        if (!isLoadingMore.current) {
          updateVisibleMonth();
        }
      }, 150);
    }
  }, [allDays.length, updateVisibleMonth]);

  // Check if we need to load more days
  const checkAndLoadMore = useCallback(() => {
    if (!scrollContainerRef.current || allDays.length === 0) return;

    const container = scrollContainerRef.current;
    const scrollLeft = container.scrollLeft;
    const scrollWidth = container.scrollWidth;
    const clientWidth = container.clientWidth;

    // Calculate how many days are left to scroll to (approximately)
    const totalScrollable = scrollWidth - clientWidth;
    const scrollProgress = scrollLeft / totalScrollable;

    // If we're within the last 10 days worth of content (roughly 85% scrolled)
    if (scrollProgress > 0.85) {
      loadMoreDays();
    }
  }, [allDays.length, loadMoreDays]);

  // Handle scroll events
  const handleScroll = useCallback(() => {
    checkAndLoadMore();
    updateVisibleMonth();
  }, [checkAndLoadMore, updateVisibleMonth]);

  // Navigation handlers with scrolling
  const SCROLL_AMOUNT = 280; // Pixels to scroll each time (about 4 dates)

  const handlePreviousWeek = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: -SCROLL_AMOUNT,
        behavior: "smooth",
      });

      // Update visible month after scrolling
      setTimeout(updateVisibleMonth, 300);
    }
  };

  const handleNextWeek = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: SCROLL_AMOUNT,
        behavior: "smooth",
      });

      // Check if we need to load more and update visible month after scrolling
      setTimeout(() => {
        checkAndLoadMore();
        updateVisibleMonth();
      }, 300);
    }
  };

  // Auto-scroll to selected date
  const scrollToSelectedDate = useCallback(() => {
    if (scrollContainerRef.current && allDays.length > 0) {
      const selectedIndex = allDays.findIndex((date) =>
        isSameDay(date, selectedDate)
      );
      if (selectedIndex !== -1) {
        const dateElement = scrollContainerRef.current.children[
          selectedIndex
        ] as HTMLElement;
        if (dateElement) {
          dateElement.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
            inline: "center",
          });
        }
      }
    }
  }, [allDays, selectedDate]);

  // Auto-scroll to selected date when it changes
  useEffect(() => {
    scrollToSelectedDate();
  }, [scrollToSelectedDate]);
  const weekDayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  // Fetch real availability from backend
  useEffect(() => {
    const fetchAvailability = async () => {
      if (!selectedSalon?.id || selectedServices.length === 0) {
        setAvailableSlots([]);
        return;
      }

      // Prevent multiple simultaneous fetches
      if (isFetchingAvailability.current) {
        return;
      }

      isFetchingAvailability.current = true;
      setLoading(true);

      try {
        const dateStr = formatDateOnly(selectedDate);
        const servicesInput = selectedServices.map((service) => {
          // Extract original service ID for availability request
          // If service.id contains a variant ID (format: "serviceId-variantId"), extract the original service ID
          let originalServiceId = service.id;
          if (service.variantId && service.id.includes("-")) {
            // Remove the variant ID suffix to get the original service ID
            originalServiceId = service.id.slice(
              0,
              -(service.variantId.length + 1)
            ); // +1 for the dash
          }

          let staffIds: string[] | null = null;
          if (service.staffIds?.length) {
            staffIds = service.staffIds;
          } else if (service.staff?.id) {
            staffIds = [service.staff.id];
          } else if (selectedStaff?.id) {
            staffIds = [selectedStaff.id];
          }

          return {
            serviceId: originalServiceId,
            staffId: staffIds,
            // Include service variant ID if a variant is selected
            serviceVariantId: service.variantId || null,
          };
        });

        const { data } = await getAvailability({
          salonId: selectedSalon.id,
          services: servicesInput,
          startDate: dateStr,
          endDate: dateStr,
        });

        if (data?.salonAvailability?.length > 0) {
          // Collect all time slots from all availability objects
          const allSlots: string[] = [];
          let reason: string | null = null;

          data.salonAvailability.forEach((availability: any) => {
            // Check for reason in availability data
            if (availability.reason) {
              reason = availability.reason;
            }

            if (availability.results && availability.results.length > 0) {
              const slots = availability.results.map((slot: any) => {
                // Convert from 24hr format to HH:MM
                const time = slot.startTime.slice(0, 5); // Get HH:MM from HH:MM:SS
                return time;
              });
              allSlots.push(...slots);
            }
          });

          if (allSlots.length > 0) {
            // Remove duplicates and sort the merged slots
            const uniqueSlots = [...new Set(allSlots)].sort();
            setAvailableSlots(uniqueSlots);
            setAvailabilityReason(null);
          } else {
            // No availability found; show empty state
            setAvailableSlots([]);
            setAvailabilityReason(reason);
          }
        } else {
          // No specific availability; show empty state
          setAvailableSlots([]);
          setAvailabilityReason(null);
        }
      } catch (error) {
        // On error, show empty state
        setAvailableSlots([]);
        setAvailabilityReason(null);
      } finally {
        setLoading(false);
        isFetchingAvailability.current = false;
      }
    };

    // Helper function to generate slots from salon working hours
    const generateSlotsFromSalonHours = () => {
      if (!selectedSalon?.salonHours) {
        setAvailableSlots([]);
        return;
      }

      const dayName = weekdayForDateOnly(selectedDate).toLowerCase();
      const todayHours = selectedSalon.salonHours.find(
        (h) => h.day.toLowerCase() === dayName
      );

      if (!todayHours || !todayHours.openTime || !todayHours.closeTime) {
        setAvailableSlots([]);
        return;
      }

      const slots = [];
      const [openHour, openMin] = todayHours.openTime.split(":").map(Number);
      const [closeHour, closeMin] = todayHours.closeTime.split(":").map(Number);

      const startMinutes = openHour * 60 + openMin;
      const endMinutes = closeHour * 60 + closeMin;
      const serviceDuration = totalDuration || 30; // Default to 30 min slots

      // Generate slots based on service duration
      for (
        let minutes = startMinutes;
        minutes + serviceDuration <= endMinutes;
        minutes += 30
      ) {
        const hour = Math.floor(minutes / 60);
        const minute = minutes % 60;
        const time = `${hour.toString().padStart(2, "0")}:${minute
          .toString()
          .padStart(2, "0")}`;
        slots.push(time);
      }

      setAvailableSlots(slots);
    };

    fetchAvailability();
  }, [
    selectedDate,
    selectedSalon?.id,
    selectedServices.map((s) => s.id).join(","),
    selectedStaff?.id,
    totalDuration,
  ]);

  // Handle next availability response
  useEffect(() => {
    if (nextAvailabilityData?.next_available_slot) {
      const slot = nextAvailabilityData.next_available_slot;
      const currentKey = `${slot.date}-${slot.start_time}-${slot.end_time}`;

      // If we've already handled this exact slot, skip re-processing
      if (handledNextAvailableKeyRef.current === currentKey) {
        setNextAvailabilityRequest(null);
        return;
      }

      handledNextAvailableKeyRef.current = currentKey;

      const nextAvailableDate = parseDateOnly(slot.date);
      if (!nextAvailableDate) {
        setNextAvailabilityRequest(null);
        return;
      }

      // Update selected date only
      setSelectedDate(nextAvailableDate);

      // Reset selected time so user must choose
      setSelectedTime("");

      // Ensure the date is in our allDays array, if not, load more days
      const isDateAvailable = allDays.some(
        (date) =>
          date.getTime() === nextAvailableDate.getTime()
      );

      if (!isDateAvailable) {
        // Load more days to include the next available date
        const loadDaysUntilDate = async () => {
          while (currentMonthEnd < nextAvailableDate) {
            await new Promise((resolve) => {
              loadMoreDays();
              setTimeout(resolve, 200);
            });
          }
        };
        loadDaysUntilDate();
      }

      // Scroll to the new date after a brief delay to ensure DOM is updated
      setTimeout(() => {
        scrollToSelectedDate();
      }, 300);

      // Reset the request to null to prevent re-triggering
      setNextAvailabilityRequest(null);
    }

    if (nextAvailabilityError) {
      setNextAvailabilityRequest(null);
    }
  }, [
    nextAvailabilityData,
    nextAvailabilityError,
    allDays,
    currentMonthEnd,
    loadMoreDays,
    scrollToSelectedDate,
  ]);

  // Update searchingNextAvailable state based on hook loading state
  useEffect(() => {
    setSearchingNextAvailable(nextAvailabilityLoading);
  }, [nextAvailabilityLoading]);

  const isPastDate = (date: Date) => {
    return date < salonToday;
  };

  const isSameDay = (date1: Date, date2: Date) => {
    return date1.getTime() === date2.getTime();
  };

  const handleDateSelect = (date: Date) => {
    if (!isPastDate(date) && !isBeyondMaxDate(date)) {
      setSelectedDate(date);
      // Clear context time when date changes (user needs to select new time)
      setContextSelectedTime(null);
      setSelectedTime(""); // Reset time when date changes
    }
  };

  const handleTimeSelect = (time: string) => {
    setSelectedTime(time);
    if (selectedDate) {
      const dateStr = formatDateOnly(selectedDate);
      const dateTime = encodeScheduledWall(dateStr, time);
      setAppointmentDateTime(dateTime);
      // Store selected date/time in context for availability polling
      setContextSelectedDate(dateStr);
      setContextSelectedTime(time);
      // Start the 5-minute slot reservation timer
      setSlotSelectionTime(Date.now());
      handleNextStep(BookingStep.PHONE_FORM);
    }
  };

  const handleBack = () => {
    // Go back to initial page if staff selection is skipped
    const previousStep = businessInfo?.allowServiceStaffSelection
      ? BookingStep.STAFF_SELECTION
      : BookingStep.INITIAL;
    handleNextStep(previousStep);
  };

  // Handle going to next available date
  const handleGoToNextAvailable = useCallback(() => {
    if (!selectedSalon?.id || selectedServices.length === 0) return;

    // Create services array in the format expected by the API
    const services: AvailabilityService[] = selectedServices.map((service) => {
      // Extract original service ID for availability request
      // If service.id contains a variant ID (format: "serviceId-variantId"), extract the original service ID
      let originalServiceId = service.id;
      if (service.variantId && service.id.includes("-")) {
        // Remove the variant ID suffix to get the original service ID
        originalServiceId = service.id.slice(
          0,
          -(service.variantId.length + 1)
        ); // +1 for the dash
      }

      return {
        service_id: originalServiceId,
        staff_id: service.staff?.id ? [service.staff.id] : undefined,
        // Include service variant ID if a variant is selected
        service_variant_id: service.variantId || undefined,
      };
    });

    // Format selected date as YYYY-MM-DD to pass to API
    // The API will search starting from the day after this date
    const dateString = selectedDate
      ? formatDateOnly(selectedDate)
      : undefined;

    const request: AvailabilityNextRequest = {
      salon_id: selectedSalon.id,
      services: services,
      date: dateString,
      _timestamp: Date.now(), // Force re-fetch even with identical parameters
    };

    // Reset handled key so we can process the next response
    handledNextAvailableKeyRef.current = null;

    // Trigger the API call by setting the request
    setNextAvailabilityRequest(request);
  }, [selectedSalon?.id, selectedServices, selectedDate]);

  // Auto-trigger next-available search after confirm race (slot/resource unavailable)
  useEffect(() => {
    if (!shouldFindNextAvailable) return;
    setShouldFindNextAvailable(false);
    handleGoToNextAvailable();
  }, [
    shouldFindNextAvailable,
    setShouldFindNextAvailable,
    handleGoToNextAvailable,
  ]);

  // Group time slots by period
  const groupTimeSlots = (slots: string[]) => {
    const morning = slots.filter((time) => {
      const hour = parseInt(time.split(":")[0]);
      return hour >= 6 && hour < 12;
    });
    const afternoon = slots.filter((time) => {
      const hour = parseInt(time.split(":")[0]);
      return hour >= 12 && hour < 17;
    });
    const evening = slots.filter((time) => {
      const hour = parseInt(time.split(":")[0]);
      return hour >= 17 && hour < 24;
    });

    return { morning, afternoon, evening };
  };

  const groupedSlots = groupTimeSlots(availableSlots);
  const currency = getBusinessCurrency(businessInfo);

  // Format time to 12-hour format with AM/PM
  const formatTimeDisplay = (time: string) => {
    const [hour, minute] = time.split(":").map(Number);
    const period = hour >= 12 ? "PM" : "AM";
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:${minute.toString().padStart(2, "0")} ${period}`;
  };

  return (
    <div className="min-h-screen bg-grey-5 pb-24 lg:pb-0">
      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Calendar and Service Summary */}
          <div className="lg:col-span-2 space-y-4">
            {/* Service Summary */}
            <div className=" border-b border-grey-100 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-grey-900">
                    Appointment Summary
                  </h3>
                  <p className="text-sm text-grey-600 mt-1">
                    {selectedServices.length} service
                    {selectedServices.length !== 1 ? "s" : ""} •
                    {formatDuration(totalDuration)} •
                    {formatPrice(
                      totalPrice,
                      currency
                    )}
                  </p>
                </div>

                {businessInfo?.allowServiceStaffSelection && (
                  <div onClick={handleBack} className="text-right">
                    <p className="text-sm text-grey-600">With</p>
                    <p className="flex items-center gap-1 cursor-pointer text-sm font-medium text-green-300 underline">
                      {(() => {
                        // Check if any services have staff assigned
                        const servicesWithStaff = selectedServices.filter(
                          (service) => service.staff
                        );

                        if (servicesWithStaff.length === 0) {
                          return "Any professional";
                        }

                        // Check if all services have the same staff
                        const firstStaff = servicesWithStaff[0]?.staff;
                        const allSameStaff = selectedServices.every(
                          (service) => service.staff?.id === firstStaff?.id
                        );

                        if (allSameStaff && firstStaff) {
                          return (
                            firstStaff.user?.fullName ||
                            firstStaff.user?.firstName ||
                            "Professional"
                          );
                        }

                        // Multiple different staff
                        const uniqueStaff = Array.from(
                          new Set(servicesWithStaff.map((s) => s.staff?.id))
                        )
                          .map(
                            (id) =>
                              servicesWithStaff.find((s) => s.staff?.id === id)
                                ?.staff
                          )
                          .filter(Boolean);

                        if (uniqueStaff.length > 1) {
                          return "Multiple professionals";
                        }

                        return (
                          uniqueStaff[0]?.user?.fullName ||
                          uniqueStaff[0]?.user?.firstName ||
                          "Professional"
                        );
                      })()}
                      <span className="text-green-300">
                        <ArrowBigRight className="h-4 w-4" />
                      </span>
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Date Selector */}
            <div className="  ">
              {/* Month/Year Header with Navigation */}
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-base font-semibold text-grey-900">
                  {selectedDate && (
                    <span>
                      {formatDateOnlyForDisplay(selectedDate, {
                        weekday: "short",
                      })}{" "}
                    </span>
                  )}
                  {allDays.length === 0
                    ? "Select Date"
                    : `${
                        monthNames[visibleMonth.getUTCMonth()]
                      } ${visibleMonth.getUTCFullYear()}`}
                </h3>
                <div className="flex items-center gap-1">
                  <button
                    onClick={handlePreviousWeek}
                    className="p-2 rounded-full transition-colors text-grey-600 hover:bg-grey-50"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    onClick={handleNextWeek}
                    className="p-2 rounded-full transition-colors text-grey-600 hover:bg-grey-50"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Date Circles - Infinite Scrolling Container */}
              <div className="relative no-scrollbar">
                <div
                  ref={scrollContainerRef}
                  onScroll={handleScroll}
                  className="flex gap-3 overflow-x-auto  no-scrollbar"
                >
                  {allDays.map((date, index) => {
                    const isSelected = isSameDay(date, selectedDate);
                    const dayName = weekDayNames[date.getUTCDay()];
                    const isPast = isPastDate(date);

                    return (
                      <div
                        key={formatDateOnly(date)}
                        className="flex flex-col items-center gap-2 flex-shrink-0"
                      >
                        <button
                          onClick={() => handleDateSelect(date)}
                          disabled={isPast}
                          className={`
                            rounded-full flex items-center justify-center font-semibold transition-all
                            w-12 h-12 text-base
                            lg:w-14 lg:h-14 lg:text-lg
                            ${
                              isSelected
                                ? "bg-green-300 text-white"
                                : isPast
                                ? "bg-grey-50 text-grey-300 cursor-not-allowed"
                                : "bg-white border-2 border-grey-100 hover:border-green-200 text-grey-900"
                            }
                          `}
                        >
                          {date.getUTCDate()}
                        </button>
                        <span
                          className={`text-xs ${
                            isSelected
                              ? "text-green-600 font-medium"
                              : "text-grey-600"
                          }`}
                        >
                          {dayName}
                        </span>
                        {/* Show month label for first date of each month */}
                        {(index === 0 || date.getUTCDate() === 1) && (
                          <span className="text-xs text-grey-500 font-medium mt-1">
                            {monthNames[date.getUTCMonth()].slice(0, 3)}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <p className="font-medium text-[12px] text-green-300">
              {salonTimeZoneLabel(salonTimezone)}
            </p>
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-grey-900 mb-4">
                  Select time
                </h3>

                {loading ? (
                  <div className="py-12 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-300 mx-auto"></div>
                    <p className="mt-3 text-sm text-grey-600">
                      Loading available times...
                    </p>
                  </div>
                ) : availableSlots.length === 0 ? (
                  <div className="py-12 text-center">
                    {availabilityReason === "NO_WORKING_HOURS" ||
                    availabilityReason === "NO_AVAILABILITY" ? (
                      <>
                        <StaffNotAvailableImage className="h-20 w-60 text-grey-300 mx-auto mb-3 rotate-12" />
                        <p className="text-grey-600 mt-10">
                          Staff Not Available
                        </p>
                      </>
                    ) : (
                      <>
                        <SvgClosed className="h-20 w-20 text-grey-300 mx-auto mb-3" />
                        <p className="text-grey-600">
                          No available times for this date
                        </p>
                      </>
                    )}
                    <Button
                      className="text-sm mt-5"
                      onClick={handleGoToNextAvailable}
                      disabled={searchingNextAvailable}
                    >
                      {searchingNextAvailable ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Finding next available date...
                        </>
                      ) : (
                        "Go to next available date"
                      )}
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {availableSlots.map((time) => (
                      <button
                        key={time}
                        onClick={() => handleTimeSelect(time)}
                        className="w-full py-3 px-4 flex items-center justify-center rounded-lg border text-left font-medium transition-all border-grey-100 hover:border-green-300 hover:bg-green-50 text-grey-700 hover:text-green-700 bg-white cursor-pointer"
                      >
                        {formatTimeDisplay(time)}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Booking summary widget */}
          <div className="space-y-4">
            <div className="sticky top-6">
              <CartSummary
                showDateTime={true}
                customButton={{
                  text: "Continue to Payment",
                  onClick: (e) => handleNextStep(BookingStep.PHONE_FORM),
                  disabled: loading,
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Mobile and Desktop Footer - Back button */}
    </div>
  );
}
